export interface GameItem {
  id?: string | number;
  name: string;
  slug: string;
  image: string;
  useCdnOnMobile?: boolean;
  pay_today?: number | null;
  ribbon?: GameRibbon | null;
  tags?: GameTag[] | null;
  provider?: { id: number; name: string } | null;
}

export interface GameSectionConfig {
  title: string;
  iconPath: string;
  games: GameItem[];
  key?: string;
  seeMoreUrl?: string;
  showNumbers?: boolean;
}

export interface BannerItem {
  image: string;
  alt: string;
  linkUrl?: string | null;
}

export interface AffiliateDetailsConfig {
  name: string;
  avatarUrl: string;
  title: string;
  promoCodePlaceholder: string;
  redeemButtonText: string;
  iconName?: string;
  /**
   * 'circle' (default): avatar redondo com borda e gradiente (perfis oficiais comuns).
   * 'icon': sem círculo/borda/gradiente — usa a imagem como ícone livre (ex.: perfil esportes com a taça da Copa).
   */
  avatarVariant?: 'circle' | 'icon';
}

export interface AffiliateConfig {
  banner: BannerItem[];
  pageTitle: string;
  smarticoMissionFilter: string[];
  smarticoMissionTitles?: string[];
  smarticoTournamentFilter?: string[];
  smarticoMinigameFilter?: string | string[];
  smarticoStoreFilter?: string[];
  originalSmarticoMissionFilter?: string[];
  showPromocodeInput: boolean;
  affiliateDetails?: AffiliateDetailsConfig;
  gameSections: GameSectionConfig[];
  pageSectionOrder: string[];
  categories: string[];
  splitMissionFilters?: boolean;
  backgroundColor?: string;
  textColor?: string;
  accentColor?: string;
  /**
   * 'default' (padrão): banner full-width fixo com avatar sobreposto (estilo perfil-oficial atual).
   * 'home': carrossel estilo HomeV2 — SliderApostatudo (desktop) / InformationBanners (mobile) — com avatar + nome abaixo do slide ativo.
   */
  bannerStyle?: 'default' | 'home';
  /**
   * Slug usado na URL e nas chamadas de API (banners, sections).
   * Quando ausente, usa a própria chave do `affiliateConfigs`.
   * Útil quando a chave precisa ser normalizada (sem hífens) mas a URL/API espera o slug original.
   * Ex.: chave `esportespagina` + `urlSlug: 'esportes-pagina'`.
   */
  urlSlug?: string;
  /**
   * Quando true, oculta a barra com avatar + nome + título do perfil que aparece abaixo do banner.
   * Usado em perfis temáticos (ex.: esportes-pagina) onde o banner já comunica a identidade.
   * Não afeta o input de promocode, que segue regra própria de `showPromocodeInput`.
   */
  hideAffiliateBar?: boolean;
  /**
   * ID da categoria Smartico que agrupa as badges do álbum (`badge.category_ids` contém este id).
   * Quando definido, o componente `AlbumCopa` constrói o álbum dinamicamente a partir das badges
   * dessa categoria, agrupando por `custom_section_id` e ordenando por `position`.
   */
  albumCategoryId?: number;
}

export const affiliateConfigs: Record<string, AffiliateConfig> = {
  saraadm: {
    banner: [
      {
        image:
          'https://imagedelivery.net/TjsK7FMOjGj8BrLfedhFqw/bff87a34-5cac-464d-aaa0-d12d4f1b3100/public',
        alt: 'Banner Sara ADM',
      },
    ],
    pageTitle: '',
    smarticoMissionFilter: ['INSTAGRAM', 'TELEGRAM'],
    smarticoMissionTitles: ['Missões Instagram', 'Missões Telegram'],
    smarticoTournamentFilter: [],
    smarticoMinigameFilter: ['803'],
    smarticoStoreFilter: [],
    showPromocodeInput: true,
    affiliateDetails: {
      name: 'Sara ADM',
      avatarUrl: getAssetsUrlForCDN(
        '/assets/images/perfil-oficial/saraadm.webp',
      ),
      title: 'ADM Oficial',
      promoCodePlaceholder: 'Digite seu código promocional',
      redeemButtonText: 'RESGATAR',
      iconName: 'perfil-oficial/embaixador-oficial',
    },
    gameSections: [
      {
        title: 'Top 10 da ADM',
        iconPath: 'perfil-oficial/crown',
        key: 'top10Games',
        seeMoreUrl: '/games/providers/pgsoft',
        showNumbers: true,
        games: [
          {
            name: 'Wild Bandito',
            slug: 'pgsoft/wild-bandito',
            image:
              'https://imagedelivery.net/TjsK7FMOjGj8BrLfedhFqw/211244a7-5827-4c55-cd1e-a58a8746de00',
          },
          {
            name: 'Fortune Snake',
            slug: 'pgsoft/fortune-snake',
            image:
              'https://imagedelivery.net/TjsK7FMOjGj8BrLfedhFqw/1a85e41e-8b2e-440e-49c7-bd345cb57100',
          },
          {
            name: 'PIZZA PIZZA PIZZA',
            slug: 'pragmaticplay/pizza-pizza-pizza',
            image:
              'https://imagedelivery.net/TjsK7FMOjGj8BrLfedhFqw/7d1242bc-cd4e-4ceb-f75d-339dbe2fe900',
          },
          {
            name: 'Cachorro Sortudo',
            slug: 'pragmaticplay/cachorro-sortudo',
            image:
              'https://imagedelivery.net/TjsK7FMOjGj8BrLfedhFqw/c9a4a451-6931-4f0e-1b67-aa7483c1b200',
          },
          {
            name: 'Wild West Duels',
            slug: 'pragmaticplay/wild-west-duels',
            image:
              'https://imagedelivery.net/TjsK7FMOjGj8BrLfedhFqw/dac59d6c-1b1e-48bc-1b48-85f1f5deb100',
          },
          {
            name: 'Hip Hop Panda',
            slug: 'pgsoft/hip-hop-panda',
            image:
              'https://imagedelivery.net/TjsK7FMOjGj8BrLfedhFqw/4dffdc7f-a5bf-45f5-d577-b7a3078a7e00',
          },
          {
            name: 'Gates of Olympus 1000',
            slug: 'pragmaticplay/gates-of-olympus-1000',
            image:
              'https://imagedelivery.net/TjsK7FMOjGj8BrLfedhFqw/b32ad1b6-d61f-48fd-8d7e-77e3472ef700',
          },
          {
            name: 'Cash Mania',
            slug: 'pgsoft/cash-mania',
            image:
              'https://imagedelivery.net/TjsK7FMOjGj8BrLfedhFqw/868d1aa3-db55-4a1e-eddc-c749f99f2b00',
          },
          {
            name: 'Plushie Wins',
            slug: 'pragmaticplay/plushie-wins',
            image:
              'https://imagedelivery.net/BgH9d8bzsn4n0yijn4h7IQ/e3de5666-ec4d-4ab2-83fa-ddfdfa52ec00',
          },
          {
            name: 'Ice Mints',
            slug: 'pragmaticplay/ice-mints',
            image:
              'https://imagedelivery.net/BgH9d8bzsn4n0yijn4h7IQ/db7f643f-7e48-4027-4af2-c13172591700',
          },
        ],
      },
      {
        title: 'Mais Jogados pela ADM',
        iconPath: 'home/mais-jogados',
        key: 'maisJogadosGames',
        seeMoreUrl: '/games/providers/pragmaticplay',
        games: [
          {
            name: 'Caramelo Sortudo',
            slug: 'amusnet/caramelo-sortudo',
            image:
              'https://imagedelivery.net/BgH9d8bzsn4n0yijn4h7IQ/e8ba26d5-f06d-48cf-311c-a1a498f3b500',
          },
          {
            name: 'Fortune Tiger',
            slug: 'pgsoft/fortune-tiger',
            image:
              'https://imagedelivery.net/TjsK7FMOjGj8BrLfedhFqw/c7fca9b3-f916-4dcd-55fd-890381650900',
          },
          {
            name: 'Wild Cash',
            slug: 'bgaming/wild-cash',
            image:
              'https://imagedelivery.net/BgH9d8bzsn4n0yijn4h7IQ/af39c3c9-7819-40f3-f645-a8bb201abe00',
          },
          {
            name: 'Ratinho Sortudo',
            slug: 'pragmaticplay/ratinho-sortudo',
            image:
              'https://imagedelivery.net/TjsK7FMOjGj8BrLfedhFqw/9f7c8802-cbd4-4156-2b1f-0e8576762500',
          },
          {
            name: 'Graffiti Rush',
            slug: 'pgsoft/graffiti-rush',
            image:
              'https://imagedelivery.net/TjsK7FMOjGj8BrLfedhFqw/b9bb6293-c372-45ca-4eb5-e6d11f945c00',
          },
          {
            name: 'Fenix Sortuda',
            slug: 'pragmaticplay/fenix-sortuda',
            image:
              'https://imagedelivery.net/TjsK7FMOjGj8BrLfedhFqw/e6106b89-cc5a-4fd0-1c65-355a0626e000',
          },
          {
            name: 'Pig Farm',
            slug: 'pragmaticplay/pig-farm',
            image:
              'https://imagedelivery.net/TjsK7FMOjGj8BrLfedhFqw/874e52e8-a4e0-45ac-47d1-3cd904dff500',
          },
          {
            name: 'Sweet Bonanza 1000',
            slug: 'pragmaticplay/sweet-bonanza-1000',
            image:
              'https://imagedelivery.net/TjsK7FMOjGj8BrLfedhFqw/91c01994-06cb-4117-4e74-aa32e6938100',
          },
          {
            name: 'Sugar Supreme Powernudge',
            slug: 'pragmaticplay/sugar-supreme-powernudge',
            image:
              'https://imagedelivery.net/TjsK7FMOjGj8BrLfedhFqw/bbd611ad-4504-4a26-118e-2012d65a3700',
          },
          {
            name: 'Pinata Wins',
            slug: 'pgsoft/pinata-wins',
            image:
              'https://imagedelivery.net/TjsK7FMOjGj8BrLfedhFqw/8e5e3ac8-676c-445e-4180-1824a7428400',
          },
        ],
      },
      {
        title: 'Jogue Junto com a ADM',
        iconPath: 'home/ganhos-turbo',
        key: 'jogueJuntoGames',
        seeMoreUrl: '/games/category/slots',
        games: [
          {
            name: 'Lucky Neko',
            slug: 'pgsoft/lucky-neko',
            image:
              'https://imagedelivery.net/TjsK7FMOjGj8BrLfedhFqw/e10247cd-5370-455f-02c8-5e4b9db7c700',
          },
          {
            name: 'Starlight Princess 1000',
            slug: 'pragmaticplay/starlight-princess-1000',
            image:
              'https://imagedelivery.net/TjsK7FMOjGj8BrLfedhFqw/7b6c1240-a9a3-4100-ffef-3e6242b39700',
          },
          {
            name: 'Futebol Fever',
            slug: 'pgsoft/futebol-fever',
            image:
              'https://imagedelivery.net/TjsK7FMOjGj8BrLfedhFqw/1cd15976-48e0-4c89-b76c-d0467f107b00',
          },
          {
            name: 'Diner Delights',
            slug: 'pgsoft/diner-delights',
            image:
              'https://imagedelivery.net/TjsK7FMOjGj8BrLfedhFqw/427618e8-6603-4d6e-0c88-51ca89e26400',
          },
          {
            name: 'Hot Fiesta',
            slug: 'pragmaticplay/hot-fiesta',
            image:
              'https://imagedelivery.net/BgH9d8bzsn4n0yijn4h7IQ/79728b26-e788-4120-c964-b786320e7100',
          },
          {
            name: 'Big Bass Reel Repeat',
            slug: 'pragmaticplay/big-bass-reel-repeat',
            image:
              'https://imagedelivery.net/BgH9d8bzsn4n0yijn4h7IQ/92a03fce-04b1-4ae9-f346-668e2b1e9c00',
          },
          {
            name: '3 Buzzing Wilds',
            slug: 'pragmaticplay/3-buzzing-wilds',
            image:
              'https://imagedelivery.net/TjsK7FMOjGj8BrLfedhFqw/e9e6b0cb-ec68-4bbf-e436-3e25a4793b00',
          },
          {
            name: 'Fortune Dragon',
            slug: 'pgsoft/fortune-dragon',
            image:
              'https://imagedelivery.net/TjsK7FMOjGj8BrLfedhFqw/a2989a83-18e6-45e3-dced-564a7bf83f00',
          },
          {
            name: 'Circus Delight',
            slug: 'pgsoft/circus-delight',
            image:
              'https://imagedelivery.net/TjsK7FMOjGj8BrLfedhFqw/f49cc208-5511-4459-2405-00b488bf1000',
          },
          {
            name: 'Sugar Rush 1000',
            slug: 'pragmaticplay/sugar-rush-1000',
            image:
              'https://imagedelivery.net/TjsK7FMOjGj8BrLfedhFqw/1079ca6c-0012-4310-dd11-37a840497c00',
          },
        ],
      },
    ],
    pageSectionOrder: [
      'banner',
      'tabs',
      'missions',
      'missions',
      'top10Games',
      'maisJogadosGames',
      'tournaments',
      'jogueJuntoGames',
      'minigames',
    ],
    categories: ['Slots', 'Gates', 'Tiger'],
    splitMissionFilters: true,
  },
  padrinho: {
    banner: [
      {
        image:
          'https://imagedelivery.net/TjsK7FMOjGj8BrLfedhFqw/b7bcd9cf-610b-450f-a5c5-d0de35174100/public',
        alt: 'Banner Padrinho',
      },
    ],
    pageTitle: '',
    smarticoMissionFilter: ['PADRINHO'],
    smarticoMissionTitles: [],
    smarticoTournamentFilter: ['NUMERIC', 'PADRINHO'],
    smarticoMinigameFilter: ['802'],
    smarticoStoreFilter: [],
    showPromocodeInput: true,
    affiliateDetails: {
      name: 'Padrinho',
      avatarUrl: getAssetsUrlForCDN(
        '/assets/images/perfil-oficial/padrinho.webp',
      ),
      title: 'Perfil Oficial',
      promoCodePlaceholder: 'Digite seu código promocional',
      redeemButtonText: 'RESGATAR',
      iconName: 'perfil-oficial/embaixador-oficial',
    },
    gameSections: [
      {
        title: 'Top 10 Jogos do Padrinho',
        iconPath: 'perfil-oficial/crown',
        key: 'top10Jogos',
        seeMoreUrl: '/games/category/all',
        showNumbers: true,
        games: [
          {
            name: 'Fortune Horse',
            slug: 'pgsoft/fortune-horse',
            image:
              'https://imagedelivery.net/TjsK7FMOjGj8BrLfedhFqw/af103085-de2c-449d-9cc2-f57867ac3200/public',
          },
          {
            name: 'Grimms Bounty: Hansel & Gretel',
            slug: 'pgsoft/grimms-bounty-hansel-gretel',
            image:
              'https://imagedelivery.net/TjsK7FMOjGj8BrLfedhFqw/1229279d-e847-408e-4c14-5d6e7c636a00/public',
          },
          {
            name: 'Knockout Riches',
            slug: 'pgsoft/knockout-riches',
            image:
              'https://imagedelivery.net/TjsK7FMOjGj8BrLfedhFqw/6c6b1c22-0227-4109-43cc-67c3e5c24d00/public',
          },
          {
            name: 'Futebol Fever',
            slug: 'pgsoft/futebol-fever',
            image:
              'https://imagedelivery.net/TjsK7FMOjGj8BrLfedhFqw/9c2a1832-56d9-4a7a-7a70-4b995ee48e00/public',
          },
          {
            name: 'Kraken Gold Rush',
            slug: 'pgsoft/kraken-gold-rush',
            image:
              'https://imagedelivery.net/TjsK7FMOjGj8BrLfedhFqw/1eca42f7-442d-46db-2b97-177f261c6700/public',
          },
          {
            name: 'Fortune Rabbit',
            slug: 'pgsoft/fortune-rabbit',
            image:
              'https://imagedelivery.net/TjsK7FMOjGj8BrLfedhFqw/45f072d3-0128-438e-4c47-bcaf7ea6e900/public',
          },
          {
            name: 'Shaolin Soccer',
            slug: 'pgsoft/shaolin-soccer',
            image:
              'https://imagedelivery.net/TjsK7FMOjGj8BrLfedhFqw/29772cce-b7f9-4e08-b502-507e98bf5300/public',
          },
          {
            name: 'Fortune Panda - CP',
            slug: 'cpgames/fortune-panda',
            image:
              'https://imagedelivery.net/TjsK7FMOjGj8BrLfedhFqw/08feccfe-fcca-4f27-ee49-c3ce1e979d00/public',
          },
          {
            name: 'Mr Treasures',
            slug: 'pgsoft/mr-treasures-fortune',
            image:
              'https://imagedelivery.net/TjsK7FMOjGj8BrLfedhFqw/1f3cf9c9-b792-426f-417b-cfdb39f13200/public',
          },
          {
            name: 'Alibabas Cave of Fortune',
            slug: 'pgsoft/alibabas-cave-of-fortune',
            image:
              'https://imagedelivery.net/TjsK7FMOjGj8BrLfedhFqw/e9dfb90d-fc83-4559-ed93-df95dff9ea00/public',
          },
        ],
      },
      {
        title: 'Top 10 Jogos do Padrinho',
        iconPath: 'perfil-oficial/crown',
        key: 'top10JogosHalloween',
        seeMoreUrl: '',
        showNumbers: true,
        games: [
          {
            name: 'Fortune Rabbit',
            slug: 'pgsoft/fortune-rabbit',
            image:
              'https://imagedelivery.net/TjsK7FMOjGj8BrLfedhFqw/f9ffc3d2-6634-42e4-7846-f3646308bd00',
          },
          {
            name: 'Shaolin Soccer',
            slug: 'pgsoft/shaolin-soccer',
            image:
              'https://imagedelivery.net/TjsK7FMOjGj8BrLfedhFqw/e5a4195f-100f-4a02-5370-7486537ed800',
          },
          {
            name: 'Spirited Wonders',
            slug: 'pgsoft/spirited-wonders',
            image:
              'https://imagedelivery.net/TjsK7FMOjGj8BrLfedhFqw/0213087d-bc6b-4b8b-71f6-a2106d51f400',
          },
          {
            name: 'Mr. Hallow-Win',
            slug: 'pgsoft/mr-hallow-win',
            image:
              'https://imagedelivery.net/TjsK7FMOjGj8BrLfedhFqw/bfcb10de-5650-4c33-84ea-f5ae39010a00',
          },
          {
            name: 'VampireS Charm',
            slug: 'pgsoft/vampires-charm',
            image:
              'https://imagedelivery.net/TjsK7FMOjGj8BrLfedhFqw/323b86f3-ebbc-490e-f6d3-64090338a200',
          },
          {
            name: 'Devil Fire 2',
            slug: 'tada/devil-fire-2',
            image:
              'https://imagedelivery.net/TjsK7FMOjGj8BrLfedhFqw/1167b70f-4de8-45d4-4705-1c3ded0a3b00',
          },
          {
            name: 'Book of Monsters',
            slug: 'pragmaticplay/book-of-monsters',
            image:
              'https://imagedelivery.net/TjsK7FMOjGj8BrLfedhFqw/18413898-4fed-470a-6eab-8491fffaa000',
          },
          {
            name: 'Big Bass Halloween',
            slug: 'pragmaticplay/big-bass-halloween',
            image:
              'https://imagedelivery.net/TjsK7FMOjGj8BrLfedhFqw/bf4e4eb4-e851-4579-e74f-3c38d6d02d00',
          },
          {
            name: 'Nightfall Hunting',
            slug: 'tada/nightfall-hunting',
            image:
              'https://imagedelivery.net/TjsK7FMOjGj8BrLfedhFqw/f7c29ce2-69a5-407a-f485-5b7678741a00',
          },
          {
            name: 'Hand Of Anubis',
            slug: 'hacksaw/hand-of-anubis',
            image:
              'https://imagedelivery.net/TjsK7FMOjGj8BrLfedhFqw/7af56c74-e420-41eb-deab-14a5527ebe00',
          },
        ],
      },
      {
        title: 'Jogos de Halloween',
        iconPath: 'home/hacksaw',
        key: 'jogosHalloween',
        seeMoreUrl: '',
        showNumbers: false,
        games: [
          {
            name: 'Fortune Tiger',
            slug: 'pgsoft/fortune-tiger',
            image:
              'https://imagedelivery.net/TjsK7FMOjGj8BrLfedhFqw/1b11b4bc-1ef8-4894-76c5-0d35a43a0700',
          },
          {
            name: 'Fortune Dragon',
            slug: 'pgsoft/fortune-dragon',
            image:
              'https://imagedelivery.net/TjsK7FMOjGj8BrLfedhFqw/184e3852-b207-44ec-7368-a38aba8c9600',
          },
          {
            name: 'Fortune Ox',
            slug: 'pgsoft/fortune-ox',
            image:
              'https://imagedelivery.net/TjsK7FMOjGj8BrLfedhFqw/cb5cc649-f3a4-4d02-91a0-e8cc908a4400',
          },
          {
            name: 'Fortune Mouse',
            slug: 'pgsoft/fortune-mouse',
            image:
              'https://imagedelivery.net/TjsK7FMOjGj8BrLfedhFqw/4179ed38-80af-4702-e171-9b70dfabb500',
          },
          {
            name: 'Wild Bandito',
            slug: 'pgsoft/wild-bandito',
            image:
              'https://imagedelivery.net/TjsK7FMOjGj8BrLfedhFqw/389d7678-24d9-43fe-4006-48edd0b4ec00',
          },
          {
            name: 'Zombie Outbreak',
            slug: 'pgsoft/zombie-outbreak',
            image:
              'https://imagedelivery.net/TjsK7FMOjGj8BrLfedhFqw/914e1be4-8952-45c6-2afa-bf73c9b6e600',
          },
          {
            name: 'Fire Portals',
            slug: 'pragmaticplay/fire-portals',
            image:
              'https://imagedelivery.net/TjsK7FMOjGj8BrLfedhFqw/a4b652ca-8234-4529-0d32-cf719f382c00',
          },
          {
            name: 'WerewolfS Hunt',
            slug: 'pgsoft/werewolfs-hunt',
            image:
              'https://imagedelivery.net/TjsK7FMOjGj8BrLfedhFqw/bb40bafa-2445-40fd-df71-dd907ec2c300',
          },
          {
            name: 'Gates Of Hades',
            slug: 'pragmaticplay/gates-of-hades',
            image:
              'https://imagedelivery.net/TjsK7FMOjGj8BrLfedhFqw/7d6243a0-3741-48b2-9e9b-70f5ea8e3f00',
          },
          {
            name: 'Witch Heart Megaways',
            slug: 'pragmaticplay/witch-heart-megaways',
            image:
              'https://imagedelivery.net/TjsK7FMOjGj8BrLfedhFqw/8c5f8c41-8aac-46fd-a682-27ad49ebd900',
          },
          {
            name: 'Devil Fire',
            slug: 'tada/devil-fire',
            image:
              'https://imagedelivery.net/TjsK7FMOjGj8BrLfedhFqw/ff861b6b-2dc9-4b17-dc4d-509402d1ce00',
          },
          {
            name: 'RIP City',
            slug: 'hacksaw/rip-city',
            image:
              'https://imagedelivery.net/TjsK7FMOjGj8BrLfedhFqw/cae4b63d-c54e-437a-9ff1-5e9deeb8bb00',
          },
          {
            name: 'SixSixSix',
            slug: 'hacksaw/sixsixsix',
            image:
              'https://imagedelivery.net/TjsK7FMOjGj8BrLfedhFqw/7c3d68f9-406f-4360-6dcd-5f63281fe700',
          },
          {
            name: 'Chaos Crew 2',
            slug: 'hacksaw/chaos-crew-2',
            image:
              'https://imagedelivery.net/TjsK7FMOjGj8BrLfedhFqw/d97a857a-3174-4149-57c5-1bda40719d00',
          },
          {
            name: 'Medusa II',
            slug: 'pgsoft/medusa-ii',
            image:
              'https://imagedelivery.net/TjsK7FMOjGj8BrLfedhFqw/c87e56c3-9c28-4220-908f-02503dfa5800',
          },
          {
            name: 'POTION WIZARD',
            slug: 'tada/potion-wizard',
            image:
              'https://imagedelivery.net/TjsK7FMOjGj8BrLfedhFqw/a6af12f1-90f1-4c72-76b4-d6aa7f1ca100',
          },
          {
            name: 'Ocean King Jackpot',
            slug: 'tada/ocean-king-jackpot',
            image:
              'https://imagedelivery.net/TjsK7FMOjGj8BrLfedhFqw/14f462ba-1e38-4447-f856-d60026a6c100',
          },
          {
            name: 'Witches Night',
            slug: 'tada/witches-night',
            image:
              'https://imagedelivery.net/TjsK7FMOjGj8BrLfedhFqw/5eb52077-57e8-4c0c-c91b-83ffb60b2100',
          },
        ],
      },
      {
        title: 'Jogos da PG Soft',
        iconPath: 'home/mais-jogados',
        key: 'jogosPG',
        seeMoreUrl: '/games/providers/pgsoft',
        showNumbers: false,
        games: [
          {
            name: 'Fortune Dragon',
            slug: 'pgsoft/fortune-dragon',
            image:
              'https://imagedelivery.net/BgH9d8bzsn4n0yijn4h7IQ/67fffbd0-2722-42c6-9e61-bf1ede490000',
          },
          {
            name: 'Fortune Ox',
            slug: 'pgsoft/fortune-ox',
            image:
              'https://imagedelivery.net/BgH9d8bzsn4n0yijn4h7IQ/d497611a-a2d4-4459-b4ac-c7998dafb400',
          },
          {
            name: 'Fortune Snake',
            slug: 'pgsoft/fortune-snake',
            image:
              'https://imagedelivery.net/BgH9d8bzsn4n0yijn4h7IQ/f73fa9d5-5204-49da-0c82-dd4b3cdc2c00',
          },
          {
            name: 'Pinata Wins',
            slug: 'pgsoft/pinata-wins',
            image:
              'https://imagedelivery.net/BgH9d8bzsn4n0yijn4h7IQ/c515a1fe-347e-4245-c203-fc0749a7e700',
          },
          {
            name: 'Fortune Mouse',
            slug: 'pgsoft/fortune-mouse',
            image:
              'https://imagedelivery.net/BgH9d8bzsn4n0yijn4h7IQ/1baadf89-53c7-4ec8-b58c-1f3abf0fe200',
          },
          {
            name: 'Dragon Hatch',
            slug: 'pgsoft/dragon-hatch',
            image:
              'https://imagedelivery.net/BgH9d8bzsn4n0yijn4h7IQ/4d63d98f-a509-402b-8812-f6f0a9396200',
          },
          {
            name: 'Wild Bandito',
            slug: 'pgsoft/wild-bandito',
            image:
              'https://imagedelivery.net/BgH9d8bzsn4n0yijn4h7IQ/dbdef422-0cdf-4e31-796f-367212030b00',
          },
          {
            name: 'Midas Fortune',
            slug: 'pgsoft/midas-fortune',
            image:
              'https://imagedelivery.net/BgH9d8bzsn4n0yijn4h7IQ/655c178e-3c66-49fb-8ed9-7e9306f53500',
          },
          {
            name: 'Wild Bounty Showdown',
            slug: 'pgsoft/wild-bounty-showdown',
            image:
              'https://imagedelivery.net/BgH9d8bzsn4n0yijn4h7IQ/f85a73de-cf04-4cf6-6d8d-b6f879aa4800',
          },
          {
            name: 'Cash Mania',
            slug: 'pgsoft/cash-mania',
            image:
              'https://imagedelivery.net/BgH9d8bzsn4n0yijn4h7IQ/9845a083-c105-48d2-ec4a-14571e09d100',
          },
        ],
      },
      {
        title: 'Top 10 Jogos Novos',
        iconPath: 'home/novidades',
        key: 'top10Novos',
        seeMoreUrl: '/games/category/all',
        showNumbers: true,
        games: [
          {
            name: 'Mr. Treasures Fortune',
            slug: 'pgsoft/mr-treasures-fortune',
            image:
              'https://imagedelivery.net/TjsK7FMOjGj8BrLfedhFqw/b5a58051-695b-4fb6-1943-78b00acf3600',
          },
          {
            name: 'Rico Toucano',
            slug: 'popok/rico-toucano',
            image:
              'https://imagedelivery.net/BgH9d8bzsn4n0yijn4h7IQ/48c4edcd-d36c-49c5-6131-5901d3bc8b00',
          },
          {
            name: 'Sweet Bonanza 1000 Dice',
            slug: 'pragmaticplay/sweet-bonanza-1000-dice',
            image:
              'https://imagedelivery.net/BgH9d8bzsn4n0yijn4h7IQ/5ec2bbd5-88d2-45e6-9729-2d73e786a700',
          },
          {
            name: 'Gravity Bonanza',
            slug: 'pragmaticplay/gravity-bonanza',
            image:
              'https://imagedelivery.net/BgH9d8bzsn4n0yijn4h7IQ/c934c61e-9a68-44f2-9fcf-ac6578789700',
          },
          {
            name: 'Gates of Valhalla',
            slug: 'pragmaticplay/gates-of-valhalla',
            image:
              'https://imagedelivery.net/BgH9d8bzsn4n0yijn4h7IQ/c78de118-7347-4c9c-be62-019bff345800',
          },
          {
            name: 'Shaolin Masters',
            slug: 'hacksaw/shaolin-master',
            image:
              'https://imagedelivery.net/BgH9d8bzsn4n0yijn4h7IQ/9fb5c4b4-4443-47f3-1c1d-beb0b4ea5600',
          },
          {
            name: 'Bomb Bonanza',
            slug: 'pragmaticplay/bomb-bonanza',
            image:
              'https://imagedelivery.net/BgH9d8bzsn4n0yijn4h7IQ/6cf8fe3e-8261-4325-adb3-651700c6e200',
          },
          {
            name: 'Starlight Princess Pachi',
            slug: 'pragmaticplay/starlight-princess-pachi',
            image:
              'https://imagedelivery.net/BgH9d8bzsn4n0yijn4h7IQ/3f97fe26-c279-4d20-521a-e46f74baed00',
          },
          {
            name: 'Chicken Drop',
            slug: 'pragmaticplay/chicken-drop',
            image:
              'https://imagedelivery.net/BgH9d8bzsn4n0yijn4h7IQ/786c3738-e8a2-49ab-101b-9a4d42847200',
          },
          {
            name: 'Jaguar Dojo',
            slug: 'popok/jaguar-dojo',
            image:
              'https://imagedelivery.net/BgH9d8bzsn4n0yijn4h7IQ/20d87075-aac6-4b99-46af-a69d37584400',
          },
        ],
      },
      {
        title: 'Jogos da Pragmatic',
        iconPath: 'home/slots-vantajosos',
        key: 'jogosPragmatic',
        seeMoreUrl: '/games/providers/pragmaticplay',
        showNumbers: false,
        games: [
          {
            name: 'Gates of Hades',
            slug: 'pragmaticplay/gates-of-hades',
            image:
              'https://imagedelivery.net/BgH9d8bzsn4n0yijn4h7IQ/f49be0ce-ea89-4919-be6b-efe3d020c500',
          },
          {
            name: 'Gates of Olympus',
            slug: 'pragmaticplay/gates-of-olympus',
            image:
              'https://imagedelivery.net/BgH9d8bzsn4n0yijn4h7IQ/854119bf-1af1-493a-56ba-2114a5554d00',
          },
          {
            name: 'Sweet Bonanza 1000',
            slug: 'pragmaticplay/sweet-bonanza-1000',
            image:
              'https://imagedelivery.net/BgH9d8bzsn4n0yijn4h7IQ/a6ab452e-bc5f-402c-0592-ce9bef564300',
          },
          {
            name: 'Tigre Sortudo 1000',
            slug: 'pragmaticplay/tigre-sortudo-1000',
            image:
              'https://imagedelivery.net/BgH9d8bzsn4n0yijn4h7IQ/9e633ca8-e535-415d-12cf-6a66a7bc3b00',
          },
          {
            name: 'Gates of Olympus 1000',
            slug: 'pragmaticplay/gates-of-olympus-1000',
            image:
              'https://imagedelivery.net/BgH9d8bzsn4n0yijn4h7IQ/1598f1f4-ee0e-46b0-d294-1286d5a1bf00',
          },
          {
            name: 'Sugar Rush 1000',
            slug: 'pragmaticplay/sugar-rush-1000',
            image:
              'https://imagedelivery.net/BgH9d8bzsn4n0yijn4h7IQ/4985fb1e-7cb9-4929-5897-d14058094600',
          },
          {
            name: 'Gates of Olympus Super Scatter',
            slug: 'pragmaticplay/gates-of-olympus-super-scatter',
            image:
              'https://imagedelivery.net/BgH9d8bzsn4n0yijn4h7IQ/4aa5aa08-61c6-47b1-0c88-1e5411f5f300',
          },
          {
            name: "Joker's Jewels",
            slug: 'pragmaticplay/jokers-jewels',
            image:
              'https://imagedelivery.net/BgH9d8bzsn4n0yijn4h7IQ/cac32397-597e-479e-42d5-a2de61b6e400',
          },
          {
            name: 'Sweet Bonanza',
            slug: 'pragmaticplay/sweet-bonanza',
            image:
              'https://imagedelivery.net/BgH9d8bzsn4n0yijn4h7IQ/67a88295-83fc-4c56-f6cf-379bd4e5dd00',
          },
          {
            name: 'Big Bass Splash',
            slug: 'pragmaticplay/big-bass-splash',
            image:
              'https://imagedelivery.net/BgH9d8bzsn4n0yijn4h7IQ/53807dfa-f77b-437b-f092-b495826e1b00',
          },
        ],
      },
      {
        title: 'Jogos da Popok',
        iconPath: 'home/ganhos-nas-alturas',
        key: 'jogosPopok',
        seeMoreUrl: '/games/providers/popok',
        showNumbers: false,
        games: [
          {
            name: 'Rico Gorila',
            slug: 'popok/rico-gorila',
            image:
              'https://imagedelivery.net/BgH9d8bzsn4n0yijn4h7IQ/edce40a3-a27d-477c-e176-da3648d18100',
          },
          {
            name: 'Yo Dragon',
            slug: 'popok/yo-dragon',
            image:
              'https://imagedelivery.net/BgH9d8bzsn4n0yijn4h7IQ/b5166e07-22e5-4f57-c2b2-ccc69245d600',
          },
          {
            name: 'Fortune Panda',
            slug: 'popok/fortune-panda',
            image:
              'https://imagedelivery.net/BgH9d8bzsn4n0yijn4h7IQ/b294b2ea-d860-4a6c-a09a-a140fba00e00',
          },
          {
            name: 'Fortune Crash',
            slug: 'popok/fortune-crash',
            image:
              'https://imagedelivery.net/BgH9d8bzsn4n0yijn4h7IQ/0c90786c-b464-4ea1-8938-d758f3570d00',
          },
          {
            name: 'Papai Noel da Fortuna',
            slug: 'popok/papai-noel-da-fortuna',
            image:
              'https://imagedelivery.net/BgH9d8bzsn4n0yijn4h7IQ/dd045688-26f8-488a-af5d-0257c72d1900',
          },
          {
            name: 'Sumo Baby',
            slug: 'popok/sumo-baby',
            image:
              'https://imagedelivery.net/BgH9d8bzsn4n0yijn4h7IQ/a5e74d8c-9f6b-4759-ae76-b7dc99940000',
          },
          {
            name: 'Book of Sherlock',
            slug: 'popok/book-of-sherlock',
            image:
              'https://imagedelivery.net/BgH9d8bzsn4n0yijn4h7IQ/b1f7b379-9e1f-4c2c-885c-a77fb3360400',
          },
          {
            name: 'Blazing Hot',
            slug: 'popok/blazing-hot',
            image:
              'https://imagedelivery.net/BgH9d8bzsn4n0yijn4h7IQ/ed81c340-f0ec-4c5c-865d-9136f9120a00',
          },
          {
            name: 'Yummy',
            slug: 'popok/yummy',
            image:
              'https://imagedelivery.net/BgH9d8bzsn4n0yijn4h7IQ/110d7c46-80ca-49a0-2de0-701d3ed91700',
          },
          {
            name: 'Lucky Jungle 1024',
            slug: 'popok/lucky-jungle-1024',
            image:
              'https://imagedelivery.net/BgH9d8bzsn4n0yijn4h7IQ/918e0cb0-0c81-44e4-87a6-f20ce9dce200',
          },
        ],
      },
      {
        title: 'Jogos da Hacksaw',
        iconPath: 'home/hacksaw',
        key: 'jogosHacksaw',
        seeMoreUrl: '/games/providers/hacksaw',
        showNumbers: false,
        games: [
          {
            name: 'Le Bandit',
            slug: 'hacksaw/le-bandit',
            image:
              'https://imagedelivery.net/TjsK7FMOjGj8BrLfedhFqw/ed8bac0c-a8b8-432c-3ba7-80089b797c00',
          },
          {
            name: 'Hand of Anubis',
            slug: 'hacksaw/hand-of-anubis',
            image:
              'https://imagedelivery.net/BgH9d8bzsn4n0yijn4h7IQ/60b530a2-b758-45e1-a85b-0f8a19af9a00',
          },
          {
            name: 'Ze Zeus',
            slug: 'hacksaw/ze-zeus',
            image:
              'https://imagedelivery.net/BgH9d8bzsn4n0yijn4h7IQ/0644074f-0de0-47c3-6780-b15713600400',
          },
          {
            name: 'RIP City',
            slug: 'hacksaw/rip-city',
            image:
              'https://imagedelivery.net/TjsK7FMOjGj8BrLfedhFqw/af9a658f-387e-4afa-d811-c0761b1d4000',
          },
          {
            name: 'SixSixSix',
            slug: 'hacksaw/sixsixsix',
            image:
              'https://imagedelivery.net/TjsK7FMOjGj8BrLfedhFqw/3146fe93-db5a-4ee5-f113-372ddcc83500',
          },
          {
            name: 'Wanted Dead or a Wild',
            slug: 'hacksaw/wanted-dead-or-a-wild',
            image:
              'https://imagedelivery.net/BgH9d8bzsn4n0yijn4h7IQ/37b9f6b8-f8c7-4800-d946-df5e6526bf00',
          },
          {
            name: 'Dork Unit',
            slug: 'hacksaw/dork-unit',
            image:
              'https://imagedelivery.net/BgH9d8bzsn4n0yijn4h7IQ/cfc4798c-de79-417f-9da1-2cbb603bec00',
          },
          {
            name: 'Fist of Destruction',
            slug: 'hacksaw/fist-of-destruction',
            image:
              'https://imagedelivery.net/BgH9d8bzsn4n0yijn4h7IQ/d73bd43e-f0b4-4dd5-f27f-9e426bee5000',
          },
          {
            name: '2 Wild 2 Die',
            slug: 'hacksaw/2-wild-2-die',
            image:
              'https://imagedelivery.net/BgH9d8bzsn4n0yijn4h7IQ/9742ddd4-0dfe-46d7-46c9-dca552e37a00',
          },
          {
            name: 'Beam Boys',
            slug: 'hacksaw/beam-boys',
            image:
              'https://imagedelivery.net/TjsK7FMOjGj8BrLfedhFqw/313192cc-ffe6-4b13-4211-e7beda9eea00',
          },
        ],
      },
      {
        title: 'No Limit',
        iconPath: 'home/provedoras',
        key: 'jogosNoLimit',
        seeMoreUrl: '/games/providers/nolimitcity',
        showNumbers: false,
        games: [
          {
            name: 'Mental 2',
            slug: 'nolimitcity/mental-2',
            image:
              'https://imagedelivery.net/BgH9d8bzsn4n0yijn4h7IQ/7806bd3b-a66b-4130-26b2-75a750fc8e00',
          },
          {
            name: 'Duck Hunters',
            slug: 'nolimitcity/duck-hunters',
            image:
              'https://imagedelivery.net/BgH9d8bzsn4n0yijn4h7IQ/0bf1c3e1-f5df-4f80-cf76-9eb014d4e500',
          },
          {
            name: 'Mental',
            slug: 'nolimitcity/mental',
            image:
              'https://imagedelivery.net/BgH9d8bzsn4n0yijn4h7IQ/8200dadb-de89-4d50-7bac-3c483c3a6d00',
          },
          {
            name: 'San Quentin 2: Death Row',
            slug: 'nolimitcity/san-quentin-2-death-row',
            image:
              'https://imagedelivery.net/BgH9d8bzsn4n0yijn4h7IQ/54fabd9d-8d03-42c5-619f-b65fb90af600',
          },
          {
            name: 'Outsourced',
            slug: 'nolimitcity/outsourced',
            image:
              'https://imagedelivery.net/BgH9d8bzsn4n0yijn4h7IQ/c3fd8c65-6ee6-4a80-e4cf-964b67672000',
          },
          {
            name: 'Nine to Five',
            slug: 'nolimitcity/nine-to-five',
            image:
              'https://imagedelivery.net/BgH9d8bzsn4n0yijn4h7IQ/2772ac46-bf68-4a13-2b3e-06395cf44800',
          },
          {
            name: 'Misery Mining',
            slug: 'nolimitcity/misery-mining',
            image:
              'https://imagedelivery.net/BgH9d8bzsn4n0yijn4h7IQ/d69f82b4-89b0-4a2e-eb2b-58a5dff2e800',
          },
          {
            name: 'Punk Rocker 2',
            slug: 'nolimitcity/punk-rocker-2',
            image:
              'https://imagedelivery.net/BgH9d8bzsn4n0yijn4h7IQ/6c066211-4792-4077-b118-ccd1e9474f00',
          },
          {
            name: 'Remember Gulag',
            slug: 'nolimitcity/remember-gulag',
            image:
              'https://imagedelivery.net/BgH9d8bzsn4n0yijn4h7IQ/695813dc-b66f-42d4-2186-087ecefa7600',
          },
          {
            name: 'Kenneth Must Die',
            slug: 'nolimitcity/kenneth-must-die',
            image:
              'https://imagedelivery.net/BgH9d8bzsn4n0yijn4h7IQ/0d66f335-7e5d-42d5-3187-5b80a67c1700',
          },
        ],
      },
      {
        title: 'Jogos da Booming',
        iconPath: 'home/ganhos-turbo',
        key: 'jogosBooming',
        seeMoreUrl: '/games/providers/booming',
        showNumbers: false,
        games: [
          {
            name: 'Diamond Hits',
            slug: 'booming/diamond-hits',
            image:
              'https://imagedelivery.net/BgH9d8bzsn4n0yijn4h7IQ/2768752b-6a64-40a0-4541-874d4c988600',
          },
          {
            name: 'Stampede Bonanza',
            slug: 'booming/stampede-bonanza',
            image:
              'https://imagedelivery.net/BgH9d8bzsn4n0yijn4h7IQ/94bc9d3b-4251-428e-a068-09e0484bfa00',
          },
          {
            name: 'Burning Classics Royal Edition',
            slug: 'booming/burning-classics-royal-edition',
            image:
              'https://imagedelivery.net/BgH9d8bzsn4n0yijn4h7IQ/3f52867c-00d7-4c5e-e265-021752c79e00',
          },
          {
            name: 'Chief Hawks Spirit Walk',
            slug: 'booming/chief-hawks-spirit-walk',
            image:
              'https://imagedelivery.net/BgH9d8bzsn4n0yijn4h7IQ/2135167a-08cd-49bd-77fb-b3e0db294000',
          },
          {
            name: 'Hot Charge',
            slug: 'booming/hot-charge',
            image:
              'https://imagedelivery.net/BgH9d8bzsn4n0yijn4h7IQ/3f05fc1c-bce4-4a93-87cb-55e77d338000',
          },
          {
            name: 'Ronaldinho Spins',
            slug: 'booming/ronaldinho-spins',
            image:
              'https://imagedelivery.net/BgH9d8bzsn4n0yijn4h7IQ/f868a38c-754a-46a3-a0d6-d2fc52750000',
          },
          {
            name: 'Howling for Gold',
            slug: 'booming/howling-for-gold',
            image:
              'https://imagedelivery.net/BgH9d8bzsn4n0yijn4h7IQ/fa1a8f02-4c1e-4bd2-cf26-31775c5ae100',
          },
          {
            name: 'Gold Gold Gold 5000',
            slug: 'booming/gold-gold-gold-5000',
            image:
              'https://imagedelivery.net/BgH9d8bzsn4n0yijn4h7IQ/e925de30-9c3d-4442-ebdb-940b357e2f00',
          },
          {
            name: 'Classic Coins',
            slug: 'booming/classic-coins',
            image:
              'https://imagedelivery.net/BgH9d8bzsn4n0yijn4h7IQ/a385db71-9b37-474b-a5ad-6b8517371a00',
          },
          {
            name: 'Holly Jolly Cash Pig',
            slug: 'booming/holly-jolly-cash-pig',
            image:
              'https://imagedelivery.net/BgH9d8bzsn4n0yijn4h7IQ/21594b99-053f-4c58-6c9b-a4df8e7d6900',
          },
        ],
      },
      {
        title: 'Jogos da BGaming',
        iconPath: 'home/novidades',
        key: 'jogosBgaming',
        seeMoreUrl: '/games/providers/bgaming',
        showNumbers: false,
        games: [
          {
            name: 'Aztec Clusters',
            slug: 'bgaming/aztec-clusters',
            image:
              'https://imagedelivery.net/BgH9d8bzsn4n0yijn4h7IQ/001b2af5-58b7-4027-648c-604ce4843400',
          },
          {
            name: "Dragon's Crash",
            slug: 'bgaming/dragons-crash',
            image:
              'https://imagedelivery.net/BgH9d8bzsn4n0yijn4h7IQ/a9152736-ba2c-4f84-7927-6e3503d91500',
          },
          {
            name: 'Snoop Dogg Dollars',
            slug: 'bgaming/snoop-dogg-dollars',
            image:
              'https://imagedelivery.net/BgH9d8bzsn4n0yijn4h7IQ/194f8c2e-0883-462b-d064-df4e38011e00',
          },
          {
            name: 'Burning Chilli X',
            slug: 'bgaming/burning-chilli-x',
            image:
              'https://imagedelivery.net/BgH9d8bzsn4n0yijn4h7IQ/1cd83f32-eabe-490a-0e91-9605f30b8000',
          },
          {
            name: 'Royal Fruits MultiLines',
            slug: 'bgaming/royal-fruits-multilines',
            image:
              'https://imagedelivery.net/BgH9d8bzsn4n0yijn4h7IQ/822109bf-dca0-41f4-d3aa-9066bcee8300',
          },
          {
            name: 'Luck & Magic',
            slug: 'bgaming/luck-magic',
            image:
              'https://imagedelivery.net/BgH9d8bzsn4n0yijn4h7IQ/a3098f70-b365-4598-6f42-de57ee2ace00',
          },
          {
            name: 'Wild West TRUEWAYS',
            slug: 'bgaming/wild-west-trueways',
            image:
              'https://imagedelivery.net/BgH9d8bzsn4n0yijn4h7IQ/39b3701d-1a9f-42ea-dae0-5791c2a70900',
          },
          {
            name: 'Space XY',
            slug: 'bgaming/space-xy',
            image:
              'https://imagedelivery.net/BgH9d8bzsn4n0yijn4h7IQ/39847059-afc7-445e-553c-dfb469998d00',
          },
          {
            name: 'Chicken Rush',
            slug: 'bgaming/chicken-rush',
            image:
              'https://imagedelivery.net/BgH9d8bzsn4n0yijn4h7IQ/8a959677-37ad-440d-7728-4e2be38e4200',
          },
          {
            name: 'Wild Cash',
            slug: 'bgaming/wild-cash',
            image:
              'https://imagedelivery.net/BgH9d8bzsn4n0yijn4h7IQ/af39c3c9-7819-40f3-f645-a8bb201abe00',
          },
          {
            name: 'Aloha King Elvis',
            slug: 'bgaming/aloha-king-elvis',
            image:
              'https://imagedelivery.net/BgH9d8bzsn4n0yijn4h7IQ/2738d725-1863-41fe-3091-cd7b5bdeaa00',
          },
          {
            name: 'Wild Tiger',
            slug: 'bgaming/wild-tiger',
            image:
              'https://imagedelivery.net/BgH9d8bzsn4n0yijn4h7IQ/42bfe18b-56ff-403a-1171-8ce88527eb00',
          },
          {
            name: 'Wild Cash x9990',
            slug: 'bgaming/wild-cash-x9990',
            image:
              'https://imagedelivery.net/BgH9d8bzsn4n0yijn4h7IQ/b072f441-fb59-41d7-c432-a57b60ba4400',
          },
          {
            name: 'Panda Luck',
            slug: 'bgaming/panda-luck',
            image:
              'https://imagedelivery.net/BgH9d8bzsn4n0yijn4h7IQ/3181fa8a-fcd4-4718-c5eb-f18311a5d100',
          },
          {
            name: 'Merge Up',
            slug: 'bgaming/merge-up',
            image:
              'https://imagedelivery.net/BgH9d8bzsn4n0yijn4h7IQ/9c421755-1e9c-49c3-1bf1-1ddbeb49e800',
          },
        ],
      },
      {
        title: 'Jogos da Banana Games',
        iconPath: 'home/mais-jogados',
        key: 'jogosBananaGames',
        seeMoreUrl: '/games/providers/banana',
        showNumbers: false,
        games: [
          {
            name: 'Aviator Crash',
            slug: 'banana/aviator-crash',
            image:
              'https://imagedelivery.net/BgH9d8bzsn4n0yijn4h7IQ/716d8d07-456f-4450-808e-eb16f1bdb700',
          },
          {
            name: 'Double Max',
            slug: 'banana/double-max',
            image:
              'https://imagedelivery.net/BgH9d8bzsn4n0yijn4h7IQ/c5e082fe-7bef-4df6-b2f6-457f4ece3a00',
          },
          {
            name: 'Banana Mines',
            slug: 'banana/banana-mines',
            image:
              'https://imagedelivery.net/BgH9d8bzsn4n0yijn4h7IQ/274602c2-fb54-472e-ac30-f2d8ce943f00',
          },
          {
            name: 'Space Crash',
            slug: 'banana/space-crash',
            image:
              'https://imagedelivery.net/BgH9d8bzsn4n0yijn4h7IQ/146c4307-a2a0-46b5-1d78-29932b349f00',
          },
          {
            name: 'Fortune Pirates',
            slug: 'banana/fortune-pirates',
            image:
              'https://imagedelivery.net/BgH9d8bzsn4n0yijn4h7IQ/51c9e2fa-1b5b-4436-e243-078bfbf3f900',
          },
          {
            name: 'Fortune Fruits',
            slug: 'banana/fortune-fruits',
            image:
              'https://imagedelivery.net/BgH9d8bzsn4n0yijn4h7IQ/c2275af5-6c3e-47a6-2bfa-f77c72ff2d00',
          },
          {
            name: 'Fuga Dos Esquilos',
            slug: 'banana/fuga-dos-esquilos',
            image:
              'https://imagedelivery.net/BgH9d8bzsn4n0yijn4h7IQ/63cb4d04-b626-469b-b2f5-fe81893b4100',
          },
          {
            name: 'Lost Planet',
            slug: 'banana/lost-planet',
            image:
              'https://imagedelivery.net/BgH9d8bzsn4n0yijn4h7IQ/f8f958ac-c33d-4e17-e103-3595bd080b00',
          },
        ],
      },
    ],
    pageSectionOrder: [
      'banner',
      'top10Jogos',
      // 'top10JogosHalloween',
      // 'jogosHalloween',
      'jogosPG',
      'missions',
      'top10Novos',
      'tournaments',
      'jogosPopok',
      'minigames',
      'jogosHacksaw',
      'jogosNoLimit',
      'jogosBooming',
      'jogosBgaming',
      'jogosBananaGames',
    ],
    categories: ['Slots', 'Graffiti', 'Shaolin'],
    splitMissionFilters: false,
  },
  esportespagina: {
    banner: [],
    pageTitle: 'Esportes',
    smarticoMissionFilter: ['SPORTBOOK'],
    smarticoTournamentFilter: ['SPORTBOOK'],
    smarticoMinigameFilter: '1543',
    smarticoStoreFilter: ['589'],
    albumCategoryId: 96,
    showPromocodeInput: true,
    affiliateDetails: {
      name: 'Esportes',
      avatarUrl: getAssetsUrlForCDN(
        '/assets/images/perfil-oficial/esportes-pagina.png',
      ),
      title: 'Perfil Oficial',
      promoCodePlaceholder: 'Digite seu código promocional',
      redeemButtonText: 'RESGATAR',
      iconName: 'perfil-oficial/embaixador-oficial',
      avatarVariant: 'icon',
    },
    gameSections: [],
    pageSectionOrder: ['banner', 'jogos-em-destaque', 'album-copa'],
    categories: ['Brasil', 'Copa', 'Mundo'],
    bannerStyle: 'home',
    urlSlug: 'esportes-pagina',
    hideAffiliateBar: true,
  },
  aviators: {
    banner: [
      {
        image:
          'https://imagedelivery.net/TjsK7FMOjGj8BrLfedhFqw/c67dd54e-366b-42f7-444a-afdd5897ec00/public',
        alt: 'Banner Crash',
      },
      {
        image:
          'https://imagedelivery.net/TjsK7FMOjGj8BrLfedhFqw/140f65d6-7eae-44c9-ca4b-7f48733ede00/public',
        alt: 'Banner Crash',
      },
    ],
    pageTitle: '',
    smarticoMissionFilter: ['CRASHTUDO'],
    smarticoMissionTitles: [],
    smarticoTournamentFilter: ['CRASHTUDO'],
    smarticoMinigameFilter: ['801'],
    smarticoStoreFilter: [],
    showPromocodeInput: true,
    affiliateDetails: {
      name: 'Aviators',
      avatarUrl: getAssetsUrlForCDN(
        '/assets/images/perfil-oficial/aviators.webp',
      ),
      title: 'Comunidade Oficial Aviators',
      promoCodePlaceholder: 'Digite seu código promocional',
      redeemButtonText: 'RESGATAR',
      iconName: 'perfil-oficial/embaixador-oficial',
    },
    gameSections: [
      {
        title: 'Top 10 nas Alturas',
        iconPath: 'home/ganhos-nas-alturas',
        key: 'topGames',
        seeMoreUrl: '/games/category/jogos-crash',
        games: [
          {
            name: 'Aviator 2',
            slug: '',
            image:
              'https://imagedelivery.net/TjsK7FMOjGj8BrLfedhFqw/dd273abc-b211-41e8-001f-eeaa75317500',
          },
          {
            name: 'Aviator',
            slug: 'spribe/aviator',
            image:
              'https://imagedelivery.net/TjsK7FMOjGj8BrLfedhFqw/8b0470e5-bb04-49f0-a4e8-59458490d100',
          },
          {
            name: 'Red Baron',
            slug: 'evolution/red-baron',
            image:
              'https://imagedelivery.net/BgH9d8bzsn4n0yijn4h7IQ/754d9192-618c-4b8d-6b0f-997f61c91a00',
          },
          {
            name: 'Aviador',
            slug: 'hype/aviador',
            image:
              'https://imagedelivery.net/TjsK7FMOjGj8BrLfedhFqw/17379bb0-f3ba-4e35-6ff9-e6235915f000',
          },
          {
            name: 'Avi',
            slug: 'popok/avi',
            image:
              'https://imagedelivery.net/TjsK7FMOjGj8BrLfedhFqw/3c1bca61-18bf-4b16-827d-fcab46a85200',
          },
          {
            name: 'JetX',
            slug: 'smartsoft/jetx',
            image:
              'https://imagedelivery.net/TjsK7FMOjGj8BrLfedhFqw/8ab8bd1c-5d29-4e28-ad70-38274b8bce00',
          },
          {
            name: 'Live - Spaceman',
            slug: 'pragmaticplay/live-spaceman',
            image:
              'https://imagedelivery.net/TjsK7FMOjGj8BrLfedhFqw/365edc65-fcf3-4ac5-4dc8-904417086e00',
          },
          {
            name: 'High Flyer',
            slug: 'pragmaticplay/high-flyer',
            image:
              'https://imagedelivery.net/TjsK7FMOjGj8BrLfedhFqw/f0eeb9ba-6c9b-435a-8a7b-c378d8f49e00',
          },
          {
            name: 'Crash Cash',
            slug: 'nsoft/crash-cash',
            image:
              'https://imagedelivery.net/TjsK7FMOjGj8BrLfedhFqw/3b86f930-bd5a-4194-f681-8536e9cdf300',
          },
          {
            name: 'Space Crash',
            slug: 'banana/space-crash',
            image:
              'https://imagedelivery.net/TjsK7FMOjGj8BrLfedhFqw/d6d25a11-5dad-4e7c-5efe-e7924e0c3d00',
          },
        ],
      },
    ],
    pageSectionOrder: ['banner', 'topGames', 'tournaments', 'missions'],
    categories: ['Crash', 'Nas Alturas'],
    splitMissionFilters: false,
  },
  // danigreen: {
  //   banner: [],
  //   pageTitle: 'Dani Green',
  //   smarticoMissionFilter: ['DANIGREEN'],
  //   smarticoMissionTitles: [],
  //   smarticoTournamentFilter: ['DANIGREEN'],
  //   smarticoMinigameFilter: ['1209'],
  //   smarticoStoreFilter: ['DANIGREEN'],
  //   showPromocodeInput: true,
  //   affiliateDetails: {
  //     name: 'Dani Green',
  //     avatarUrl: getAssetsUrlForCDN(
  //       '/assets/images/perfil-oficial/danigreen.webp',
  //     ),
  //     title: 'Perfil Oficial',
  //     promoCodePlaceholder: 'Digite seu código promocional',
  //     redeemButtonText: 'RESGATAR',
  //     iconName: 'perfil-oficial/embaixador-oficial',
  //   },
  //   gameSections: [],
  //   pageSectionOrder: ['banner'],
  //   categories: ['Roletas', 'PlayTech', 'Evolution'],
  //   backgroundColor: '#000000',
  //   textColor: '#FFFFFF',
  //   accentColor: '#FF3700',
  // },
  giovanni00: {
    banner: [],
    pageTitle: 'Giovanni00',
    smarticoMissionFilter: ['GIOVANNI00'],
    smarticoMissionTitles: [],
    smarticoTournamentFilter: ['GIOVANNI00'],
    smarticoMinigameFilter: ['1209'],
    smarticoStoreFilter: ['GIOVANNI00'],
    showPromocodeInput: true,
    affiliateDetails: {
      name: 'Giovanni00',
      avatarUrl: getAssetsUrlForCDN(
        '/assets/images/perfil-oficial/giovanni00.webp',
      ),
      title: 'Perfil Oficial',
      promoCodePlaceholder: 'Digite seu código promocional',
      redeemButtonText: 'RESGATAR',
      iconName: 'perfil-oficial/embaixador-oficial',
    },
    gameSections: [],
    pageSectionOrder: ['banner'],
    categories: ['Roletas', 'Imaginelive', 'Pragmatic'],
    backgroundColor: '#000000',
    textColor: '#FFFFFF',
    accentColor: '#FF3700',
  },
  zeroumroleta: {
    banner: [],
    pageTitle: 'Zeroumroleta',
    smarticoMissionFilter: ['ZEROUMROLETA'],
    smarticoMissionTitles: [],
    smarticoTournamentFilter: ['ZEROUMROLETA'],
    smarticoMinigameFilter: ['1209'],
    smarticoStoreFilter: ['ZEROUMROLETA'],
    showPromocodeInput: true,
    affiliateDetails: {
      name: 'Zeroumroleta',
      avatarUrl: getAssetsUrlForCDN(
        '/assets/images/perfil-oficial/zeroumroleta.webp',
      ),
      title: 'Perfil Oficial',
      promoCodePlaceholder: 'Digite seu código promocional',
      redeemButtonText: 'RESGATAR',
      iconName: 'perfil-oficial/embaixador-oficial',
    },
    gameSections: [],
    pageSectionOrder: ['banner'],
    categories: ['Roletas', 'Evolution', 'Pragmatic'],
    backgroundColor: '#000000',
    textColor: '#FFFFFF',
    accentColor: '#FF3700',
  },
  fantasma: {
    banner: [],
    pageTitle: 'Fantasma',
    smarticoMissionFilter: ['FANTASMA'],
    smarticoMissionTitles: [],
    smarticoTournamentFilter: ['FANTASMA'],
    smarticoMinigameFilter: ['1209'],
    smarticoStoreFilter: [],
    showPromocodeInput: true,
    affiliateDetails: {
      name: 'Fantasma',
      avatarUrl: getAssetsUrlForCDN(
        '/assets/images/perfil-oficial/fantasma.webp',
      ),
      title: 'Perfil Oficial',
      promoCodePlaceholder: 'Digite seu código promocional',
      redeemButtonText: 'RESGATAR',
      iconName: 'perfil-oficial/embaixador-oficial',
    },
    gameSections: [],
    pageSectionOrder: [
      'banner',
      'top10Jogos',
      'missions',
      'tournaments',
      'minigames',
      'vip',
    ],
    categories: ['Imaginelive', 'Pragmatic', 'Ao vivo'],
    backgroundColor: '#000000',
    textColor: '#FFFFFF',
    accentColor: '#FF3700',
  },
};
